//#region Imports

// @angular - Core Angular imports
import {CommonModule} from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
} from '@angular/core';

// @ionic - Ionic Framework for mobile UI components
import {IonicModule} from '@ionic/angular';

// @capacitor - Native mobile functionality via Capacitor plugins
import {ScreenOrientation} from '@capacitor/screen-orientation';
import {CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';

// 3rd party - Swiper library for touch-enabled sliders/carousels
import {register} from 'swiper/element/bundle';

// Register Swiper custom elements globally for use throughout the mobile app
// This enables <swiper-container> and <swiper-slide> elements in templates
register();

//#endregion

/**
 * Mobile Application Root Component
 *
 * This is the root component of the Enterwait mobile application. It serves as the top-level
 * container for the entire mobile app and handles global initialization tasks.
 *
 * Key Responsibilities:
 * - Initialize Capacitor plugins and native device features
 * - Configure global app settings (screen orientation, status bar, splash screen)
 * - Provide the root Ionic routing outlet for navigation
 * - Handle app lifecycle events (pause, resume, background)
 * - Register global custom elements (Swiper for carousels)
 *
 * Mobile-Specific Features:
 * - Locks screen orientation to portrait mode for consistent UX
 * - Integrates Ionic Framework for native-like mobile UI components
 * - Uses OnPush change detection for optimized mobile performance
 * - Supports Swiper custom elements for touch-based image/content sliders
 *
 * Capacitor Integration:
 * - ScreenOrientation plugin: Controls device screen orientation
 * - Additional Capacitor plugins can be initialized here (Status Bar, Splash Screen, etc.)
 *
 * Template Structure:
 * - Contains <ion-app> wrapper for Ionic mobile app
 * - Provides <ion-router-outlet> for Ionic navigation stack
 * - Hosts global mobile-specific UI elements (toasts, modals, loading overlays)
 *
 * @selector ew-mobile-root
 * @standalone Component using Angular standalone architecture
 */
@Component({
  selector: 'ew-mobile-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [CommonModule, IonicModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // Required for Swiper custom elements
})
export class AppComponent implements OnInit, OnDestroy {
  /** Local override key — `'light' | 'dark' | 'system'` */
  private static readonly THEME_KEY = 'ew-theme-pref';

  /** Tracks the system color-scheme query so we can respond to OS-level
   *  changes while the user is on `'system'`. Cleaned up in destroy. */
  private mediaQuery: MediaQueryList | null = null;
  private mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

  //#region Lifecycle hooks

  /**
   * Component initialization lifecycle hook
   *
   * Performs critical mobile app initialization tasks that must complete
   * before the app is fully interactive. This includes:
   * - Locking screen orientation to portrait mode
   * - Wiring up the design-system dark/light theme
   * - Initializing Capacitor plugins
   *
   * @returns Promise that resolves when initialization is complete
   */
  async ngOnInit(): Promise<void> {
    this.initTheme();
    await this.lockOrientation();
  }

  ngOnDestroy(): void {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
  }

  //#endregion

  //#region Theme

  /**
   * Wire the dark-mode toggle for the entire app.
   *
   * Three modes drive `body.dark`:
   *   - `'dark'`   → always dark
   *   - `'light'`  → always light
   *   - `'system'` (default) → follow `prefers-color-scheme`
   *
   * The user's choice is stored in `localStorage` under `ew-theme-pref`
   * — a settings screen can call `applyTheme()` later to flip it. We
   * also listen on the media query so an OS-level change while the
   * user is on `'system'` repaints immediately.
   */
  private initTheme(): void {
    const pref = (localStorage.getItem(AppComponent.THEME_KEY) ?? 'system') as
      | 'light'
      | 'dark'
      | 'system';

    this.applyTheme(pref);

    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaListener = () => {
        const current = (localStorage.getItem(AppComponent.THEME_KEY) ?? 'system') as
          | 'light'
          | 'dark'
          | 'system';
        if (current === 'system') this.applyTheme('system');
      };
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }
  }

  /**
   * Toggle `body.dark` (Ionic) and `[data-theme]` on `<html>` (parity with
   * the web app's tokens) according to the active preference.
   */
  public applyTheme(pref: 'light' | 'dark' | 'system'): void {
    const wantDark =
      pref === 'dark' ||
      (pref === 'system' &&
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-color-scheme: dark)').matches);

    document.body.classList.toggle('dark', wantDark);
    document.documentElement.setAttribute(
      'data-theme',
      wantDark ? 'dark' : 'light',
    );
    localStorage.setItem(AppComponent.THEME_KEY, pref);
  }

  //#endregion

  //#region Screen orientation management

  /**
   * Locks the device screen orientation to portrait mode
   *
   * Prevents the app from rotating to landscape mode, ensuring a consistent
   * user experience across all screens. Portrait mode is enforced because:
   * - The app UI is designed primarily for vertical scrolling
   * - Bottom tab navigation works best in portrait orientation
   * - Most user interactions (booking, browsing) are optimized for portrait
   * - Consistent orientation reduces layout complexity and testing burden
   *
   * Uses the Capacitor ScreenOrientation plugin to interact with native
   * iOS and Android screen orientation APIs.
   *
   * Platform Support:
   * - iOS: Uses UIInterfaceOrientationMask
   * - Android: Uses ActivityInfo.screenOrientation
   *
   * @returns Promise that resolves when orientation is locked
   * @throws Error if the plugin is not available or permission is denied
   */
  public async lockOrientation(): Promise<void> {
    await ScreenOrientation.lock({orientation: 'portrait'});
  }

  //#endregion
}
